// @flow

import React, { useEffect } from 'react';
import type { Node } from 'react';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { parseHash } from 'shared_services/riseart/utils/RouteUtils';
import { withCreateRsvp } from 'shared_components/common/event/Rsvp';
import { EventsRsvpProvider } from 'shared_data/providers/rsvp/Event';
import { RsvpContext } from 'shared_data/providers/rsvp/Context';

/**
 * useRsvpHashAction
 *
 * @returns {Object}
 */
function useRsvpHashAction(): Object {
  const { translatedLocation } = useUrlParams();
  const { event, rsvp } = parseHash(translatedLocation.hash);

  return {
    eventId: event,
    status: parseInt(rsvp, 10),
  };
}

/**
 * CreateRsvpComponent
 *
 * @param {Object} props
 * @returns {null}
 */
function CreateRsvpComponent({
  createRsvp,
  userId,
  actionSetEventRsvpStatus,
  onLoading,
  loaded: loadedRsvpList,
  eventsList,
}: Object): null {
  const { eventId, status } = useRsvpHashAction();

  useEffect(() => {
    if (loadedRsvpList && userId && eventId && status !== undefined) {
      const hasOnLoading = typeof onLoading === 'function';

      if (hasOnLoading) {
        onLoading(true, { eventId, status });
      }

      createRsvp(status, eventId)
        .then(() => {
          let trackedEventData = { id: eventId };

          // Provided list with events. Find the event by id if present in the list
          if (eventsList && Array.isArray(eventsList)) {
            const [foundEvent] = eventsList.filter((item) => item.id === eventId);

            if (foundEvent) {
              trackedEventData = foundEvent;
            }
            // Provided a single event. Check if provided event data is same as the one to RSVP to
          } else if (eventsList && typeof eventsList === 'object' && eventsList.id === eventId) {
            trackedEventData = eventsList;
          }

          actionSetEventRsvpStatus({
            event: trackedEventData,
            selectedStatus: status,
          });
          if (hasOnLoading) {
            onLoading(false, { eventId, status });
          }
        })
        .catch(() => {
          if (hasOnLoading) {
            onLoading(false, { eventId, status });
          }
        });
    }
    // eslint-disable-next-line
  }, [loadedRsvpList]);
  return null;
}

const CreateRsvpAction = withCreateRsvp(CreateRsvpComponent);

/**
 * CreateRsvpProvider
 *
 * @param {Object} ownProps
 */
export const CreateRsvpProvider = (ownProps: Object): Node => (
  <EventsRsvpProvider>
    <RsvpContext.Consumer>
      {(rsvpProps) => <CreateRsvpAction {...ownProps} {...rsvpProps} />}
    </RsvpContext.Consumer>
  </EventsRsvpProvider>
);
